import ReactGA from 'react-ga4';
import { ANALYTICS_EVENT_NAMES, EVENT_TAXONOMY } from './eventTaxonomy';
import { createPageViewPayload } from './routeTracking';
import { sanitizeEventPayload, sanitizeLegacyEvent } from './sanitizeEventPayload';
import { AnalyticsEventName, AnalyticsEventPayloadByName } from './eventTypes';
import { diagnosticsCollector } from './diagnostics/diagnosticsCollector';
import { enqueueBackendAnalyticsEvent } from './backendAnalytics';

const PROD_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-ST18H510WL';
const DEV_ID = process.env.REACT_APP_GA_MEASUREMENT_ID_DEV || '';
const IS_PROD = process.env.NODE_ENV === 'production';
const ANALYTICS_FLAG = process.env.REACT_APP_ENABLE_ANALYTICS;
const DEBUG_MODE_STORAGE_KEY = 'atlas_analyticsDebugMode';

let analyticsInitialized = false;
let initializationAttempted = false;

export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

function isValidMeasurementId(measurementId: string): boolean {
  return /^G-[A-Z0-9]{8,12}$/.test(measurementId);
}

function resolveMeasurementId(): string {
  if (IS_PROD) {
    return PROD_ID;
  }
  return DEV_ID;
}

export function isAnalyticsEnabled(): boolean {
  if (ANALYTICS_FLAG === 'true') {
    return true;
  }
  if (ANALYTICS_FLAG === 'false') {
    return false;
  }
  return isValidMeasurementId(resolveMeasurementId());
}

export function isAnalyticsDebugModeEnabled(): boolean {
  if (IS_PROD) {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(DEBUG_MODE_STORAGE_KEY) === 'true';
}

function safeSetUserId(userId: string): void {
  try {
    ReactGA.set({ user_id: userId });
  } catch {
    // noop
  }
}

export function initAnalytics(userId?: string): boolean {
  if (analyticsInitialized) {
    if (userId) {
      safeSetUserId(userId);
    }
    return true;
  }

  if (initializationAttempted) {
    return analyticsInitialized;
  }

  initializationAttempted = true;

  if (!isAnalyticsEnabled()) {
    return false;
  }

  const measurementId = resolveMeasurementId();
  if (!isValidMeasurementId(measurementId)) {
    return false;
  }

  try {
    ReactGA.initialize(measurementId);
    analyticsInitialized = true;
    if (userId) {
      safeSetUserId(userId);
    }
    return true;
  } catch {
    analyticsInitialized = false;
    return false;
  }
}

function debugLog(eventName: string, payload: Record<string, unknown>): void {
  if (!isAnalyticsDebugModeEnabled()) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info('[analytics]', eventName, payload);
}

function deriveObjectHints(payload: Record<string, unknown>): { objectType?: string; objectId?: string } {
  const pairs: Array<{ key: string; type: string }> = [
    { key: 'map_id', type: 'map' },
    { key: 'run_id', type: 'simulation_run' },
    { key: 'layer_id', type: 'layer' },
    { key: 'fleet_id', type: 'fleet' },
    { key: 'service_id', type: 'service' },
    { key: 'customer_id', type: 'customer' },
    { key: 'user_id', type: 'user' },
  ];
  for (const pair of pairs) {
    const value = payload[pair.key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return { objectType: pair.type, objectId: String(value) };
    }
  }
  return {};
}

export function trackAnalyticsEvent<TEventName extends AnalyticsEventName>(
  eventName: TEventName,
  payload: AnalyticsEventPayloadByName[TEventName]
): void {
  const sanitizedPayload = sanitizeEventPayload(eventName, payload);
  const payloadRecord = sanitizedPayload as Record<string, unknown>;
  const objectHints = deriveObjectHints(payloadRecord);
  diagnosticsCollector.addAnalyticsEvent(eventName, payloadRecord, 'typed');
  enqueueBackendAnalyticsEvent(eventName, payloadRecord, objectHints);
  debugLog(eventName, sanitizedPayload as Record<string, unknown>);

  if (!analyticsInitialized) {
    initAnalytics();
  }

  if (!analyticsInitialized) {
    return;
  }

  try {
    ReactGA.event(eventName, sanitizedPayload as Record<string, unknown>);
  } catch {
    // noop
  }
}

export function logEvent(event: AnalyticsEvent): void {
  const sanitized = sanitizeLegacyEvent(event);
  const eventName = `legacy.${sanitized.category}.${sanitized.action}`;
  diagnosticsCollector.addAnalyticsEvent(eventName, sanitized as Record<string, unknown>, 'legacy');
  enqueueBackendAnalyticsEvent(eventName, sanitized as Record<string, unknown>, deriveObjectHints(sanitized as Record<string, unknown>));
  debugLog('legacy_event', sanitized);

  if (!analyticsInitialized) {
    initAnalytics();
  }

  if (!analyticsInitialized) {
    return;
  }

  try {
    ReactGA.event(sanitized as AnalyticsEvent);
  } catch {
    // noop
  }
}

export function logPageView(path?: string): void {
  trackAnalyticsEvent(
    ANALYTICS_EVENT_NAMES.page_view,
    createPageViewPayload(path || window.location.pathname, typeof document !== 'undefined' ? document.title : undefined)
  );
}

export function getAnalyticsStateForTests() {
  return {
    analyticsInitialized,
    initializationAttempted,
    measurementId: resolveMeasurementId(),
    analyticsEnabled: isAnalyticsEnabled(),
    hasValidMeasurementId: isValidMeasurementId(resolveMeasurementId()),
    eventTaxonomy: EVENT_TAXONOMY,
  };
}

export function resetAnalyticsStateForTests() {
  analyticsInitialized = false;
  initializationAttempted = false;
}
